import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {User} from '../../../entity/model/user';
import {Project} from '../../../entity/model/project';
import {Link, useNavigate} from 'react-router-dom';
import {Organization} from '../../../entity/model/organization';
import {County} from '../../../entity/model/county';
import {getStatus, OrderStatus} from '../../../entity/enums/order-status';
import {useService} from '../../../infrastructure/service/service';
import {UserPermission} from '../../../entity/enums/user-permission';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {ChartTitle, getStatusByChartTitle} from '../../../entity/enums/chart-title';
import {ChartStatus} from '../../../entity/resource/chart-status';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2'
import ProfileCard from '../components/authentication/ProfileCard';
import LoadingComponent from '../components/widget/LoadingComponent';
import ProjectDialog from '../components/project/ProjectDialog';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DueChart from '../components/chart/DueChart';
import StatusChart from '../components/chart/StatusChart';
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import moment from 'moment';
import {SearchType} from '../../../entity/enums/search-type';
import StatusWidget from '../components/widget/StatusWidget';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {toast} from 'react-toastify';
import ConfirmDialog from '../components/widget/ConfirmDialog';
import TablePagination from '../components/widget/TablePagination';
import {ProjectStats} from '../../../entity/resource/project-stats';
import {SearchOption} from '../../../entity/dto/search-dto';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Form, Formik, FormikProps} from 'formik';
import AddTaskIcon from '@mui/icons-material/AddTask';
import {SearchInputDto} from '../../../entity/dto/search-input-dto';
import {getProjectColumnHeader, ProjectColumn} from '../../../entity/enums/project-column';
import {SortType} from '../../../entity/enums/sort-type';
import {State} from '../../../entity/model/state';
import EntryDialog from '../components/project/EntryDialog';
import {UserRole} from '../../../entity/enums/user-role';

import '../../../assets/css/style.css';

const Dashboard = (): JSX.Element => {
  const {userService, projectService, organizationService, countyService, searchService, stateService} = useService();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);

  const [trigger, setTrigger] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [createProjectPermission, setCreateProjectPermission] = useState<boolean>(false);
  const [openProjectDialog, setOpenProjectDialog] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
  const [openOnHoldConfirmDialog, setOpenOnHoldConfirmDialog] = useState<boolean>(false);
  const [loadingOnHoldConfirmBtn, setLoadingOnHoldConfirmBtn] = useState<boolean>(false);
  const [openFromOnHoldConfirmDialog, setOpenFromOnHoldConfirmDialog] = useState<boolean>(false);
  const [loadingFromOnHoldConfirmBtn, setLoadingFromOnHoldConfirmBtn] = useState<boolean>(false);
  const [manageMode, setManageMode] = useState<ManageMode>();
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [chartTitle, setChartTitle] = useState<ChartTitle>();
  const [statusChartData, setStatusChartData] = useState<any>();
  const [statusChartStatus, setStatusChartStatus] = useState<ChartStatus[]>([]);
  const [dueChartData, setDueChartData] = useState<ChartStatus[]>([]);
  const [projectStats, setProjectStats] = useState<ProjectStats>();
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortMap, setSortMap] = useState<Map<ProjectColumn, SortType>>(new Map());
  const [openEntryDialog, setOpenEntryDialog] = useState<boolean>(false);
  const [entryMode, setEntryMode] = useState<ManageMode>(ManageMode.CREATE);

  const pageSize = 100;
  const tableHeaders = [
    'Order Number',
    'Parcel Count',
    'Status',
    'Due Date',
    'Searcher',
    'Search Type',
    'Companies',
    'Address',
    'State',
    'County'
  ];

  const statsChartOptions = {
    onClick: (event: any, chartElements: any[]) => {
      if (chartElements.length > 0) {
        const clickedElementIndex = chartElements[0].index;
        const clickedLabel = statusChartData.labels[clickedElementIndex];

        const updatedData = {
          ...statusChartData,
          datasets: statusChartData.datasets.map((dataset: any) => ({
            ...dataset
          }))
        };

        setTrigger(true);
        setChartTitle(clickedLabel);
        setStatusChartData(updatedData);
      }
    }
  };

  useEffect(() => {
    projectService.getCache() && projectService.setCache(false);
  }, []);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setTrigger(false);
      setLoading(true);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      if(!user?.organizationID) {
        navigate('/self-registration');
        return;
      }

      if(user?.roles.includes(UserRole.GRANT_AI_USER) || user?.roles.includes(UserRole.GRANT_AI_ADMIN)) {
        navigate('/grant-ai');
        return;
      }

      setCreateProjectPermission(userService.isPermitted(UserPermission.CREATE_PROJECT));

      const stats = await projectService.getProjectStats(user?.organizationID ?? '');
      setProjectStats(stats);

      const dashboardStats = await projectService.getStatusChartData(stats?.status);
      setStatusChartData(dashboardStats.stats);
      setStatusChartStatus(dashboardStats.status);

      const dueStats = await projectService.getDueChartData(stats?.dueDate);
      setDueChartData(dueStats);

      setTrigger(true);

      const response = await projectService.searchProjects(user?.organizationID ?? '', currentPage, pageSize, undefined, getSearchOptions());
      if (response && response.projects && response.pageSize > 0) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      setUsers(users);

      const organizations = await organizationService.getOrganizations(user?.organizationID);
      setOrganizations(organizations);

      const states = await stateService.getStates();
      setStates(states);

      const counties = await countyService.getCounties();
      setCounties(counties);
    }

    if (!projectService.getCache())
      init();
  }, [projectService.getCache()])

  useEffect(() => {
    const init = async (): Promise<void> => {
      if (chartTitle) {
        setLoading(true);
        setCurrentPage(1);

        const user = await userService.getCurrentUser();

        const response = await projectService.searchProjects(user?.organizationID ?? '', currentPage, pageSize, '', getSearchOptions(chartTitle));
        if (response && response.projects) {
          const lastPageSize = response.totalCount % response.pageSize;
          const pageCount = Math.trunc(response.totalCount / response.pageSize);

          setProjects(response.projects);
          setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
        }

        setLoading(false);
      }
    }

    init();
  }, [chartTitle]);

  const getSearchOptions = (chartTitle?: ChartTitle): SearchOption[] => {
    if (!chartTitle)
      return [
        {
          field: 'status',
          value: getStatusByChartTitle(ChartTitle.OPEN_ORDERS)
        },
        {
          field: 'status',
          value: getStatusByChartTitle(ChartTitle.PENDING_REVIEW_ORDERS)
        },
        {
          field: 'status',
          value: getStatusByChartTitle(ChartTitle.PENDING_APPROVAL_ORDERS)
        },
        {
          field: 'status',
          value: getStatusByChartTitle(ChartTitle.ON_HOLD_ORDERS)
        }
      ];

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() - 6);

    const startOfNextWeek = new Date(startOfWeek);
    startOfNextWeek.setDate(startOfWeek.getDate() + 7);

    const startOfWeekAfterNext = new Date(startOfNextWeek);
    startOfWeekAfterNext.setDate(startOfNextWeek.getDate() + 7);

    switch (chartTitle) {
    case ChartTitle.PAST_DUE:
      return [
        {
          field: 'dueDateRange',
          conditions: [
            {
              operator: 'lt',
              value: today.toISOString()
            }
          ]
        }
      ];

    case ChartTitle.TODAY_DUE:
      return [
        {
          field: 'dueDateRange',
          conditions: [
            {
              operator: 'gte',
              value: today.toISOString()
            },
            {
              operator: 'lt',
              value: tomorrow.toISOString()
            }
          ]
        }
      ];

    case ChartTitle.DUE_THIS_WEEK:
      return [
        {
          field: 'dueDateRange',
          conditions: [
            {
              operator: 'gte',
              value: startOfWeek.toISOString()
            },
            {
              operator: 'lt',
              value: startOfNextWeek.toISOString()
            }
          ]
        }
      ];

    case ChartTitle.DUE_NEXT_WEEK:
      return [
        {
          field: 'dueDateRange',
          conditions: [
            {
              operator: 'gte',
              value: startOfNextWeek.toISOString()
            },
            {
              operator: 'lt',
              value: startOfWeekAfterNext.toISOString()
            }
          ]
        }
      ];

    case ChartTitle.DUE_LATER_THIS_MONTH:
      return [
        {
          field: 'dueDateRange',
          conditions: [
            {
              operator: 'gte',
              value: startOfWeekAfterNext.toISOString()
            }
          ]
        }
      ];

    default:
      return [
        {
          field: 'status',
          value: getStatusByChartTitle(chartTitle)
        }
      ];
    }
  }

  const handleAssignPermission = (): boolean => {
    return selectedProject
      ? (userService.isPermitted(UserPermission.ASSIGN_PROJECT) && selectedProject.assignedToOrganizationID === currentUser?.organizationID) || (currentUser?.id === selectedProject.createdByUserID)
      : false;
  }

  const handleUpdatePermission = (): boolean => {
    return selectedProject
      ? userService.isPermitted(UserPermission.UPDATE_PROJECT) && projectService.isPermittedProject(selectedProject.id)
      : false;
  }

  const handleDeletePermission = (): boolean => {
    if (!currentUser)
      return false;

    if (currentUser.organizationID !== selectedProject?.createdByOrganizationID)
      return false;

    if (selectedProject?.status === getStatus(OrderStatus.ON_HOLD))
      return false;

    if (selectedProject)
      return userService.isPermitted(UserPermission.DELETE_PROJECT) && (currentUser.id === selectedProject.createdByUserID || userService.isAdminUser());

    return false;
  }

  const handleDeleteAllPermission = (project: Project): boolean => {
    if (!currentUser)
      return false;

    if (currentUser.organizationID !== project?.createdByOrganizationID)
      return false;

    if (project)
      return userService.isPermitted(UserPermission.DELETE_PROJECT) && (currentUser.id === project.createdByUserID || userService.isAdminUser());

    return false;
  }

  const handleOnHoldPermission = (isHold: boolean): boolean => {
    if (isHold && selectedProject?.status === getStatus(OrderStatus.ON_HOLD))
      return false;

    if (!isHold && selectedProject?.status !== getStatus(OrderStatus.ON_HOLD))
      return false;

    return currentUser?.organizationID === selectedProject?.createdByOrganizationID && (userService.isAdminUser() || selectedProject?.createdByUserID === currentUser?.id);
  }

  const handleDeleteProject = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true)
  }

  const deleteProjects = async (): Promise<void> => {
    setLoadingConfirmBtn(true);

    if (selected.length > 1) {
      const selectedProjects = projects.filter(project => selected.includes(project.id));

      for (let i = 0; i < selectedProjects.length; i++)
        await projectService.deleteProjects(selectedProjects[i].createdByOrganizationID, selectedProjects[i].id, i === selectedProjects.length - 1);

    } else
      await projectService.deleteProjects(selectedProject?.createdByOrganizationID ?? '', selectedProject?.id ?? '', true);

    setSelected([]);
    setLoadingConfirmBtn(false);
    setOpenConfirmDialog(false);
  }

  const handleSearch = async (event: React.KeyboardEvent<HTMLDivElement>, searchInput: string): Promise<void> => {
    if (event.key === 'Enter') {
      setLoading(true);
      setCurrentPage(1);

      const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, searchInput, getSearchOptions());
      if (response && response.projects) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);
    }
  }

  const handleCreateProject = (): void => {
    setOpenProjectDialog(true);
    setManageMode(ManageMode.CREATE);
  }

  const handleUpdateProject = (): void => {
    handleMenuClose();
    setOpenProjectDialog(true);
    setManageMode(ManageMode.UPDATE);
  }

  const handleAssignProject = (): void => {
    handleMenuClose();
    setOpenEntryDialog(true);
    setEntryMode(ManageMode.ASSIGN);
  }

  const handleComingSoonProject = (): void => {
    handleMenuClose();
    toast.info('Coming Soon ...');
  }

  const handleMoveToOnHold = async (): Promise<void> => {
    handleMenuClose();
    setOpenOnHoldConfirmDialog(true);
  }

  const confirmMoveToOnHold = async (): Promise<void> => {
    setLoadingOnHoldConfirmBtn(true);

    await projectService.moveProjectToOnHold(currentUser?.organizationID ?? '', selectedProject?.id ?? '');

    setOpenOnHoldConfirmDialog(false);
    setLoadingOnHoldConfirmBtn(false);
    window.location.reload();
  }

  const handleMoveFromOnHold = async (): Promise<void> => {
    handleMenuClose();
    setOpenFromOnHoldConfirmDialog(true);
  }

  const confirmMoveFromOnHold = async (): Promise<void> => {
    setLoadingFromOnHoldConfirmBtn(true);

    await projectService.moveProjectFromOnHold(currentUser?.organizationID ?? '', selectedProject?.id ?? '');

    setOpenFromOnHoldConfirmDialog(false);
    setLoadingFromOnHoldConfirmBtn(false);
    window.location.reload();
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const newSelected = projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).map((project: Project): string => project.id);
      setSelected(newSelected);
      return;
    }

    setSelected([]);
  }

  const handleSelect = (event: React.MouseEvent<unknown>, id: string): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1)
      newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0)
      newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1)
      newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0)
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));

    setSelected(newSelected);
  }

  const disableSelectAll = (): boolean => {
    for (const project of projects)
      if (handleDeleteAllPermission(project))
        return false;

    return true;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: Project): void => {
    setMenuElement(event.currentTarget);
    setSelectedProject(project);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handlePaginationChange = async (event: React.ChangeEvent<unknown>, activePage: number): Promise<void> => {
    setLoading(true);
    setCurrentPage(activePage);

    const response = await projectService.searchProjects(currentUser?.organizationID ?? '', activePage, pageSize, undefined, getSearchOptions());
    if (response && response.projects)
      setProjects(response.projects);

    setLoading(false);
  }

  const refreshPage = async (): Promise<void> => {
    setRefresh(true);

    const stats = await projectService.getProjectStats(currentUser?.organizationID ?? '');
    setProjectStats(stats);

    const dashboardStats = await projectService.getStatusChartData(stats?.status);
    setStatusChartData(dashboardStats.stats);
    setStatusChartStatus(dashboardStats.status);

    const dueStats = await projectService.getDueChartData(stats?.dueDate);
    setDueChartData(dueStats);

    const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, undefined, getSearchOptions());
    if (response && response.projects) {
      const lastPageSize = response.totalCount % response.pageSize;
      const pageCount = Math.trunc(response.totalCount / response.pageSize);

      setProjects(response.projects);
      setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
    }

    setRefresh(false);
  }

  const handleSort = async (projectColumn?: ProjectColumn): Promise<void> => {
    if (projectColumn) {
      setLoading(true);
      setCurrentPage(1);

      let sortDirection = SortType.ASC;
      const newMap = new Map();
      const sortItem = sortMap.get(projectColumn);

      if (sortItem)
        sortDirection = sortItem === SortType.ASC ? SortType.DESC : SortType.ASC;

      newMap.set(projectColumn, sortDirection);
      setSortMap(newMap);

      const response = await projectService.searchProjects(currentUser?.organizationID ?? '', currentPage, pageSize, '', getSearchOptions(), projectColumn, sortDirection);
      if (response && response.projects) {
        const lastPageSize = response.totalCount % response.pageSize;
        const pageCount = Math.trunc(response.totalCount / response.pageSize);

        setProjects(response.projects);
        setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
      }

      setLoading(false);
    }
  }

  const getSortDirection = (header: string): 'asc' | 'desc' => {
    const orderColumn = getProjectColumnHeader(header);
    if (orderColumn) {
      const sortItem = sortMap.get(orderColumn);
      if (sortItem)
        return sortItem === SortType.ASC ? 'asc' : 'desc';
    }

    return 'desc';
  }

  const getCompanyName= (organizations: Organization[], project: Project): string  => {
    const createdOrganizationName = organizations.find((organization: Organization): boolean => organization.id === project.createdByOrganizationID)?.name;
    const assignedOrganizationName = organizations.find((organization: Organization): boolean => organization.id === project.assignedToOrganizationID)?.name;

    if (project.assignedToOrganizationID === project.createdByOrganizationID)
      return createdOrganizationName ?? '';

    return createdOrganizationName + ' / ' + assignedOrganizationName;
  }

  const viewEntryDialog = (project: Project): void => {
    setSelectedProject(project);
    setOpenEntryDialog(true);
    setEntryMode(ManageMode.VIEW);
  }

  const renderTableRow = (project: Project, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={project.id}
        selected={selected.includes(project.id)}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-dashboard-project-menu-${index}`}
            className={`zitles-menu-trigger ${selected.length > 1 ? 'disabled' : ''}`}
            color='inherit'
            disableRipple
            disableFocusRipple
            disabled={selected.length > 1}
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, project)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            <MenuItem
              id={`menu-project-view-${index}`}
              disableRipple
              className={'zitles-menu-item'}
              component={Link}
              to={`/organization/${selectedProject?.createdByOrganizationID}/title/${selectedProject?.titleIDs[0]}`}
              onClick={handleMenuClose}>
              <VisibilityIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>View</Typography>
            </MenuItem>
            {
              handleUpdatePermission() &&
                <MenuItem id={`menu-project-edit-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleUpdateProject}>
                  <EditIcon fontSize={'small'} sx={{marginLeft: '5px'}} className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Edit</Typography>
                </MenuItem>
            }
            {
              handleAssignPermission() &&
                <MenuItem id={`menu-project-assign-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleAssignProject}>
                  <PersonAddAltIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Assign</Typography>
                </MenuItem>
            }
            {
              handleOnHoldPermission(true) &&
                <MenuItem
                  id={`menu-project-hold-${index}`}
                  disableRipple
                  className={'zitles-menu-item'}
                  onClick={handleMoveToOnHold}>
                  <StopCircleIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Put On Hold</Typography>
                </MenuItem>
            }
            {
              handleOnHoldPermission(false) &&
                <MenuItem
                  id={`menu-project-reopen-${index}`}
                  disableRipple
                  className={'zitles-menu-item'}
                  onClick={handleMoveFromOnHold}>
                  <PlayArrowIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Reopen</Typography>
                </MenuItem>
            }
            <MenuItem id={`menu-project-cancel-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleComingSoonProject}>
              <CancelIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Cancel</Typography>
            </MenuItem>
            {
              handleDeletePermission() &&
                <Box>
                  <Divider/>
                  <MenuItem id={`menu-project-delete-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleDeleteProject}>
                    <DeleteIcon className={'zitles-icon'}/>
                    <Typography className={'zitles-menu-item button'}>Delete</Typography>
                  </MenuItem>
                </Box>
            }
          </Menu>
        </TableCell>
        <TableCell className={`no-wrap table-checkbox body table-cell-checkbox ${!handleDeleteAllPermission(project) ? 'disabled' : ''}`} padding={'none'}>
          <Checkbox
            id={`checkbox-project-${index}`}
            disabled={!handleDeleteAllPermission(project)}
            checked={selected.includes(project.id)}
            onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleSelect(event, project.id)}/>
        </TableCell>
        <TableCell className={'no-wrap'} align={'left'}>
          <Link
            id={`text-project-name-${index}`}
            className={'table-cell-link'}
            to={`/organization/${project.createdByOrganizationID}/title/${project.titleIDs[0]}`}>
            {project.orderNumber}
          </Link>
        </TableCell>
        <TableCell id={`text-project-parcel-count-${index}`} className={'no-wrap table-cell-text link'} align={'left'} onClick={(): void => viewEntryDialog(project)}>
          {project.titleIDs.length} Parcels
        </TableCell>
        <TableCell id={`text-project-status-${index}`} className={'no-wrap'} align={'left'}>
          <StatusWidget
            status={Object.values(ChartTitle).find((chartTitle: ChartTitle): boolean => chartTitle === (OrderStatus[project.status as keyof typeof OrderStatus] as unknown)) || ChartTitle.COMPLETED_ORDERS}
            table/>
        </TableCell>
        <TableCell id={`text-project-due-date-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            moment.utc(project.dueDate).format('YYYY-MM-DD')
          }
        </TableCell>
        <TableCell id={`text-project-abstractor-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, project?.assignedToUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-project-search-type-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            SearchType[project.searchType as keyof typeof SearchType]
          }
        </TableCell>
        <TableCell id={`text-project-organization-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            organizations.length > 0
              ? getCompanyName(organizations, project)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-project-address-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            project.address ?? 'Multiple'
          }
        </TableCell>
        <TableCell id={`text-project-state-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            states.length > 0
              ? project.stateID === null ? '' : states.find((state: State): boolean => state.id === project.stateID)?.shortName ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-project-county-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            counties.length > 0
              ? project.countyID === null ? '' : counties.find((county: County): boolean => county.id === project.countyID)?.name ?? ''
              : <Skeleton/>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        !loading
          ? <Box>
            {
              refresh &&
                <LoadingComponent/>
            }
            <Box className={'stats-container'}>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 3.1}}>
                  <ProfileCard
                    dueToday={projectStats?.dueDate.dueToday ?? 0}
                    dueThisWeek={projectStats?.dueDate.dueThisWeek ?? 0}/>
                </Grid>
                <Grid size={{xs: 12, sm: 12, md: 12, lg: 6, xl: 3.6}}>
                  <StatusChart
                    data={statusChartData}
                    options={statsChartOptions}
                    orderCount={0}
                    status={statusChartStatus}/>
                </Grid>
                <Grid size={{xs: 12, sm: 12, md: 12, lg: 12, xl: 5.3}}>
                  <DueChart data={dueChartData} setChartTitle={setChartTitle}/>
                </Grid>
              </Grid>
            </Box>
            <Paper className={'table-container'} elevation={0}>
              <Box className={'toolbar-container'}>
                {
                  createProjectPermission &&
                    <Button
                      id={'btn-create-project'}
                      className={'zitles-button'}
                      variant='contained'
                      startIcon={<AddIcon />}
                      disabled={!createProjectPermission}
                      onClick={handleCreateProject}>
                      New File
                    </Button>
                }
                <Formik initialValues={searchService.getSearchInputDto()} onSubmit={(): void => {}}>
                  {({values, handleChange}: FormikProps<SearchInputDto>): JSX.Element => (
                    <Form>
                      <TextField
                        id={'search-bar-dashboard'}
                        name='searchInput'
                        className={createProjectPermission ? 'zitles-searchbar' : 'zitles-searchbar no-padding'}
                        variant={'outlined'}
                        placeholder="Search"
                        onChange={handleChange}
                        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): Promise<void> => handleSearch(event, values.searchInput)}
                        value={values.searchInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon className={'zitles-searchbar icon'}/>
                            </InputAdornment>)
                        }}/>
                    </Form>
                  )}
                </Formik>
                {
                  selected.length > 1 &&
                    <Button
                      id={'btn-delete-projects'}
                      disableElevation
                      disableRipple
                      className={'toolbar-button center-end'}
                      variant='contained'
                      startIcon={<DeleteIcon />}
                      onClick={(): void => setOpenConfirmDialog(true)}>
                      Delete
                    </Button>
                }
                {
                  selected.length < 2 &&
                    <Button
                      id={'btn-dashboard-refresh'}
                      className={'zitles-button zitles-table refresh'}
                      variant='contained'
                      startIcon={<RefreshIcon />}
                      disabled={refresh}
                      onClick={refreshPage}>
                      Refresh
                    </Button>
                }
                <Button
                  id={'btn-completed-projects'}
                  disableElevation
                  disableRipple
                  className={'toolbar-button end'}
                  variant="contained"
                  startIcon={<AddTaskIcon />}
                  component={Link}
                  to={'/completed-files'}>
                  Completed Files
                </Button>
                <Button
                  id={'btn-section-templates'}
                  disableElevation
                  disableRipple
                  className={'toolbar-button end'}
                  variant="contained"
                  startIcon={<SettingsOutlinedIcon />}
                  component={Link}
                  to={'/section-templates'}>
                  Section Templates
                </Button>
              </Box>
              {
                trigger
                  ? !loading && projects.length > 0
                    ? <TableContainer className={'zitles-table'} component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow className={'table-header'}>
                              <TableCell className={'no-wrap'} padding='checkbox'/>
                              <TableCell className={'no-wrap table-cell-checkbox'} padding={'none'}>
                                <Checkbox
                                  id={'checkbox-dashboard-table'}
                                  className={`table-checkbox header ${disableSelectAll() ? 'disabled' : ''}`}
                                  indeterminate={selected.length > 0 && selected.length < projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).length}
                                  checked={!disableSelectAll() && projects.length > 0 && selected.length === projects.filter((project: Project): boolean => handleDeleteAllPermission(project)).length}
                                  onChange={handleSelectAll}
                                  disabled={disableSelectAll()}/>
                              </TableCell>
                              {
                                tableHeaders.map((header: string): JSX.Element => (
                                  <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                    <TableSortLabel
                                      id={`column-dashboard-${header}`}
                                      className={'table-header-text'}
                                      active={true}
                                      direction={getSortDirection(header)}
                                      onClick={(): Promise<void> => handleSort(getProjectColumnHeader(header))}>
                                      {header}
                                    </TableSortLabel>
                                  </TableCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {
                              projects.map(renderTableRow)
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    : <Typography id={'empty-text-dashboard-table'} className='empty-message'>Create a template and new file to get started!</Typography>
                  : loading &&
                    <LoadingComponent/>
              }
            </Paper>
            {
              trigger && !loading && projects.length > 0 &&
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePaginationChange}/>
            }
          </Box>
          : <LoadingComponent/>
      }
      {
        openProjectDialog &&
          <ProjectDialog
            open={openProjectDialog}
            setOpen={setOpenProjectDialog}
            mode={manageMode}
            project={selectedProject}/>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={`Delete File${selected.length > 1 ? 's' : ''}`}
            message={`Are you sure you want to delete ${selected.length > 1 ? selected.length + ' files?' : 'this file?'}`}
            handleSubmit={deleteProjects}
            loading={loadingConfirmBtn}/>
      }
      {
        openEntryDialog && selectedProject &&
          <EntryDialog
            open={openEntryDialog}
            setOpen={setOpenEntryDialog}
            mode={entryMode}
            project={selectedProject}/>
      }
      {
        openOnHoldConfirmDialog && selectedProject &&
          <ConfirmDialog
            open={openOnHoldConfirmDialog}
            setOpen={setOpenOnHoldConfirmDialog}
            title={'Put On Hold'}
            message={`Are you sure you want to put on hold the file "${selectedProject.orderNumber}"?`}
            submitText='Holding'
            handleSubmit={confirmMoveToOnHold}
            loading={loadingOnHoldConfirmBtn}/>
      }
      {
        openFromOnHoldConfirmDialog && selectedProject &&
          <ConfirmDialog
            open={openFromOnHoldConfirmDialog}
            setOpen={setOpenFromOnHoldConfirmDialog}
            title={'Reopen File'}
            message={`Are you sure you want to reopen the file "${selectedProject.orderNumber}"?`}
            submitText='Reopening'
            handleSubmit={confirmMoveFromOnHold}
            loading={loadingFromOnHoldConfirmBtn}/>
      }
    </>
  );
}

export default observer(Dashboard);
